// @flow weak

import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';

import type { Node } from 'react';
import { type curriedBemify } from 'bemify-js';

// This should probably be moved to a higher-order component.
export const autoCenter = ref => {
  const node = ReactDOM.findDOMNode(ref);
  const $node = $(node);
  const width = $node.width();
  const height = $node.height();
  const parentWidth = $node.parent().width();
  const windowHeight = $(window).height();

  const left = (parentWidth - width) / 2;
  const top = (windowHeight - height) / 2;
  $node.css('left', left + 'px');
  $node.css('top', top + 'px');
};

export const cloneComponentChild = (child, extraProps) => {
  if (!child) {
    return null;
  }

  if (_.isFunction(child)) {
    return child(extraProps);
  }

  return React.cloneElement(child, extraProps);
};

export const cloneComponentChildren = (
  children,
  extraProps,
  arrayContainer = 'div'
) => {
  if (!children) {
    return null;
  }

  if (_.isFunction(children)) {
    return children(extraProps);
  }

  if (!_.isArray(children)) {
    return React.cloneElement(children, extraProps || {});
  }

  const clones = React.Children.map(children, child => {
    return React.cloneElement(child, extraProps);
  });

  if (_.isFunction(arrayContainer)) {
    return arrayContainer(clones);
  }

  return React.createElement(arrayContainer, {}, clones);
};

export const childOrWrapChildren = (children, arrayContainer = 'div') => {
  if (!children) {
    return null;
  }

  if (_.isFunction(children)) {
    return children();
  }

  if (!_.isArray(children)) {
    return children;
  }

  return React.createElement(arrayContainer, {}, ...children);
};

export const isStatelessComponent = Component => {
  const isFunction = _.isFunction(Component);
  // If it's not a function, it's probably not a component
  if (!isFunction) {
    return false;
  }

  const hasPrototypeRender = Component.prototype && Component.prototype.render;
  // Stateless component either:
  // - Don't have a prototype
  // - Have a prototype, but don't have a render method
  if (hasPrototypeRender) {
    return false;
  }

  return true;
};

export const setRef = (Component, key) => nodeOrComponent => {
  // The ref might be a real DOM node _or_ a representation of a component
  _.set(Component, ['dangerousRefs', key], nodeOrComponent);
};

export const getRef = (Component, key) =>
  // Regardless of whether the ref is a real DOM node or a component,
  // always return the representative DOM node.
  ReactDOM.findDOMNode(_.get(Component, ['dangerousRefs', key]));

// Iterates over the inverse of `elementNameToComponent` and creates
// `__element` names via `bem` for each item. For instance, if
// `elementNameToComponent` is `{ __element: Element }` then if
// `Element` is one of the `children`, it will have a `className`
// prop of `bem('__element')` added to it, *in addition to* its
// possibly-already-existing `className` prop.
export const buildNodeElementizer = (
  bem: curriedBemify,
  elementNameToComponent: {
    [string]: (Object) => Node,
  }
) => {
  const componentToElementName = new Map();
  Object.keys(elementNameToComponent).forEach(elementName => {
    const component = elementNameToComponent[elementName];
    componentToElementName.set(component, elementName);
  });
  return children => {
    return React.Children.map(children, child => {
      const elementName =
        child && child.type ? componentToElementName.get(child.type) : null;
      return !elementName
        ? child
        : React.cloneElement(child, {
            className: bem(elementName, child.props.className),
          });
    });
  };
};

const getDisplayName = WrappedComponent =>
  WrappedComponent.displayName || WrappedComponent.name || 'UnknownComponent';

export const makeWrappedDisplayName = (displayName, WrappedComponent) =>
  `${displayName}(${getDisplayName(WrappedComponent)})`;

export const preventDefaultEvent = (event: SyntheticEvent<HTMLElement>) => {
  event.preventDefault();
};
